/**
 * Says out loud that this is not production.
 *
 * Staging is only safe if nobody mistakes it for the real thing, and the way
 * that mistake actually happens is not dramatic: the two screens look the same,
 * someone has both open, and they act on the wrong tab. So the marking has to be
 * impossible to miss and impossible to lose track of — it stays fixed at the top
 * of the viewport rather than scrolling away with the page, and it names the
 * host, because "am I on staging" is usually really "which of these tabs is it".
 *
 * Orange, not red: red is what this app uses for a BAD verdict and a failing
 * station, and a permanent red stripe teaches people to read red as decoration.
 *
 * Renders nothing in production. Nothing to dismiss either — a banner you can
 * close is a banner that is closed exactly when it mattered.
 */
export function EnvironmentBanner({
  environment,
  label,
}: {
  environment: string;
  label?: string;
}) {
  if (environment === 'production') return null;

  const name = environment === 'staging' ? 'STAGING' : environment.toUpperCase();

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: '#c2410c',
        color: '#fff7ed',
        borderBottom: '2px solid #f97316',
        font: '600 12px/1 ui-sans-serif, system-ui, sans-serif',
        letterSpacing: '0.14em',
        padding: '9px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        textAlign: 'center',
      }}
    >
      <span>⚠ {name}</span>
      <span style={{ opacity: 0.85, letterSpacing: '0.05em', fontWeight: 500 }}>
        {label || 'Test environment — data here is not real and may be wiped'}
      </span>
      <span style={{ opacity: 0.6, letterSpacing: '0.05em', fontWeight: 500 }}>
        {window.location.host}
      </span>
    </div>
  );
}

/**
 * The height the banner occupies, so the app shell can push itself down instead
 * of hiding its first row underneath a fixed element.
 */
export const BANNER_HEIGHT = 33;
